import { create } from 'zustand';
import { GameState, HistoryEntry } from '../types';

// Callback for when a number is set (used by GamePage to trigger completion check)
type NumberSetCallback = (cell: number, num: number, newState: string) => void;
let onNumberSetCallback: NumberSetCallback | null = null;

export const setOnNumberSetCallback = (callback: NumberSetCallback | null) => {
  onNumberSetCallback = callback;
};

interface GameStore extends GameState {
  // Actions
  initGame: (puzzle: string, solution: string, savedState?: Partial<GameState>) => void;
  selectCell: (index: number | null) => void;
  setNumber: (num: number) => void;
  toggleNote: (num: number) => void;
  toggleNoteMode: () => void;
  clearCell: () => void;
  undo: () => void;
  redo: () => void;
  useHint: (position: number, value: number) => void;
  incrementMistake: () => void;
  setTimeSpent: (time: number) => void;
  togglePause: () => void;
  checkCompletion: () => boolean;
  reset: () => void;
}

const initialState: GameState = {
  puzzle: '',
  solution: '',
  currentState: '',
  notes: {},
  selectedCell: null,
  isNoteMode: false,
  history: [],
  historyIndex: -1,
  timeSpent: 0,
  hintsUsed: 0,
  mistakes: 0,
  isComplete: false,
  isPaused: false,
};

export const useGameStore = create<GameStore>((set, get) => ({
  ...initialState,

  initGame: (puzzle, solution, savedState) => {
    const currentState = savedState?.currentState || puzzle;
    const initialHistory: HistoryEntry = {
      state: currentState,
      notes: savedState?.notes || {},
    };

    set({
      puzzle,
      solution,
      currentState,
      notes: savedState?.notes || {},
      selectedCell: null,
      isNoteMode: false,
      history: [initialHistory],
      historyIndex: 0,
      timeSpent: savedState?.timeSpent || 0,
      hintsUsed: savedState?.hintsUsed || 0,
      mistakes: savedState?.mistakes || 0,
      isComplete: false,
      isPaused: false,
    });
  },

  selectCell: (index) => {
    const { puzzle } = get();
    // Can only select non-fixed cells
    if (index !== null && puzzle[index] !== '0') {
      // Allow selecting fixed cells for highlighting related cells
      set({ selectedCell: index });
    } else {
      set({ selectedCell: index });
    }
  },

  setNumber: (num) => {
    const { selectedCell, currentState, puzzle, notes, history, historyIndex, isNoteMode } = get();
    if (selectedCell === null || puzzle[selectedCell] !== '0') return;

    if (isNoteMode) {
      get().toggleNote(num);
      return;
    }

    // Update state
    const newState = currentState.split('');
    newState[selectedCell] = num.toString();
    const newStateStr = newState.join('');

    // Clear notes for this cell
    const newNotes = { ...notes };
    delete newNotes[selectedCell];

    // Add to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ state: newStateStr, notes: newNotes });

    set({
      currentState: newStateStr,
      notes: newNotes,
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });

    // Notify callback if registered (for server-side validation)
    if (onNumberSetCallback) {
      onNumberSetCallback(selectedCell, num, newStateStr);
    }
  },

  toggleNote: (num) => {
    const { selectedCell, puzzle, notes, history, historyIndex } = get();
    if (selectedCell === null || puzzle[selectedCell] !== '0') return;

    const cellNotes = notes[selectedCell] || [];
    const newCellNotes = cellNotes.includes(num)
      ? cellNotes.filter(n => n !== num)
      : [...cellNotes, num].sort();

    const newNotes = { ...notes };
    if (newCellNotes.length === 0) {
      delete newNotes[selectedCell];
    } else {
      newNotes[selectedCell] = newCellNotes;
    }

    // Add to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ state: get().currentState, notes: newNotes });

    set({
      notes: newNotes,
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });
  },

  toggleNoteMode: () => {
    set(state => ({ isNoteMode: !state.isNoteMode }));
  },

  clearCell: () => {
    const { selectedCell, currentState, puzzle, notes, history, historyIndex } = get();
    if (selectedCell === null || puzzle[selectedCell] !== '0') return;

    const newState = currentState.split('');
    newState[selectedCell] = '0';
    const newStateStr = newState.join('');

    const newNotes = { ...notes };
    delete newNotes[selectedCell];

    // Add to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ state: newStateStr, notes: newNotes });

    set({
      currentState: newStateStr,
      notes: newNotes,
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });
  },

  undo: () => {
    const { historyIndex, history } = get();
    if (historyIndex <= 0) return;

    const newIndex = historyIndex - 1;
    const entry = history[newIndex];

    set({
      currentState: entry.state,
      notes: entry.notes,
      historyIndex: newIndex,
    });
  },

  redo: () => {
    const { historyIndex, history } = get();
    if (historyIndex >= history.length - 1) return;

    const newIndex = historyIndex + 1;
    const entry = history[newIndex];

    set({
      currentState: entry.state,
      notes: entry.notes,
      historyIndex: newIndex,
    });
  },

  useHint: (position, value) => {
    const { currentState, notes, history, historyIndex, hintsUsed } = get();

    const newState = currentState.split('');
    newState[position] = value.toString();
    const newStateStr = newState.join('');

    const newNotes = { ...notes };
    delete newNotes[position];

    // Add to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ state: newStateStr, notes: newNotes });

    set({
      currentState: newStateStr,
      notes: newNotes,
      history: newHistory,
      historyIndex: newHistory.length - 1,
      hintsUsed: hintsUsed + 1,
      selectedCell: position,
    });

    get().checkCompletion();
  },

  incrementMistake: () => {
    set(state => ({ mistakes: state.mistakes + 1 }));
  },

  setTimeSpent: (time) => {
    set({ timeSpent: time });
  },

  togglePause: () => {
    set(state => ({ isPaused: !state.isPaused }));
  },

  checkCompletion: () => {
    const { currentState, solution } = get();
    
    // Check if all cells are filled
    if (currentState.includes('0')) return false;
    
    // Check if solution is correct
    const isComplete = currentState === solution;
    if (isComplete) {
      set({ isComplete: true });
    }
    return isComplete;
  },

  reset: () => {
    const { puzzle, solution } = get();
    get().initGame(puzzle, solution);
  },
}));

